/**
 * Cards — card-GUI for the PDF a11y editor.
 *
 * Public API:
 *   blockToCard(block)                    — IR block → UI card descriptor
 *   buildCards(ir)                        — IR Document → Card[]
 *   renderCardList(container, cards, …)   — full DOM render
 *   updateCardEl(cardEl, card, …)         — targeted re-render
 *   openTypePopover(anchor, …)            — show the hierarchical role popover
 *   closeTypePopover()                    — close it
 *   ROLE_TREE                             — TEXT / VISUAL / OTHER groupings
 *
 * Pure DOM module; no global state, no IR mutations. All state changes go
 * through the host's onOp(op) callback so editor.js can pipe through the
 * existing `applyPowerEditorOp()` dispatcher.
 *
 * @module cards
 */
import { cssEscape } from "./dom-utils.js";
import { nextEditorInsertId } from "./insert-geometry.js";
import { log } from "./log.js";
import { modeFromEvent } from "./multi-select.js";
import { ROLE_UNDEFINED } from "./role-constants.js";
import { ROLE_CSS_VAR } from "../shared/role-palette.js";
// ─── Role taxonomy (TEXT / VISUAL ELEMENTS / OTHER) ─────────────────────────
/**
 * Hierarchical role list shared by single-card type-chip popover AND
 * the right-click "Change type" submenu. One source of truth.
 *
 * Each row has:
 *   role  — IR role token (matches ir.js ROLES). Every role listed here MUST
 *           round-trip coerce→block→GT-export (invariant CRP-1, editor-create-
 *           role-parity Phase 2): exposed ⊆ supported. Roles with NO backing IR
 *           block kind (the former phantom MEDIA roles + a standalone list item)
 *           were removed 260814 rather than left to silently downgrade to /P.
 *   label — short human-readable name shown in the popover.
 *   desc  — plain-language description that absorbs the cheatsheet's role.
 *   color — CSS var name for the type-chip color. Sourced from the shared
 *           `ROLE_CSS_VAR` map in role-palette.ts (single source of truth —
 *           the left-pane PDF-overlay boxes in overlays.ts read the SAME
 *           map so a box always matches its card's color). Bracket access +
 *           `!` below: every role listed here is a known-present key in
 *           ROLE_CSS_VAR (role-palette.ts is the canonical role list); the
 *           non-null assert satisfies noUncheckedIndexedAccess.
 */
export const ROLE_TREE = Object.freeze([
    {
        section: "TEXT",
        items: [
            { role: "H1", label: "H1", desc: "Top-level heading (page or section title)", color: ROLE_CSS_VAR["H1"] },
            { role: "H2", label: "H2", desc: "Heading level 2", color: ROLE_CSS_VAR["H2"] },
            { role: "H3", label: "H3", desc: "Heading level 3", color: ROLE_CSS_VAR["H3"] },
            { role: "P", label: "Paragraph", desc: "Body text", color: ROLE_CSS_VAR["P"] },
            { role: "L", label: "List", desc: "Bulleted or numbered list", color: ROLE_CSS_VAR["L"] },
        ],
    },
    {
        section: "VISUAL ELEMENTS",
        items: [
            { role: "Figure", label: "Figure", desc: "Image, diagram, or chart needing alt text", color: ROLE_CSS_VAR["Figure"] },
            { role: "Table", label: "Table", desc: "Tabular data", color: ROLE_CSS_VAR["Table"] },
            { role: "Caption", label: "Caption", desc: "Caption for a figure or table", color: ROLE_CSS_VAR["Caption"] },
            { role: "Formula", label: "Formula", desc: "Mathematical expression", color: ROLE_CSS_VAR["Formula"] },
            { role: "Span", label: "Span", desc: "Inline span / generic group", color: ROLE_CSS_VAR["Span"] },
        ],
    },
    {
        section: "OTHER",
        items: [
            { role: "Note", label: "Note", desc: "Footnote or annotation", color: ROLE_CSS_VAR["Note"] },
            { role: "Artifact", label: "Artifact", desc: "Decorative content (skipped by screen reader)", color: ROLE_CSS_VAR["Artifact"] },
        ],
    },
]);
/** Roles that carry a description (alt-text-style) field when expanded. */
// Audio/Video pruned 260814 alongside their removal from ROLE_TREE (Phase-2
// fork 2a / REVISE 5): a description role with no exposed create-role is a
// dangling reference.
export const DESCRIPTION_ROLES = Object.freeze(new Set(["Figure", "Table", "Formula"]));
/**
 * The effective description shown for a description-bearing card: the `/Alt`
 * text when present, else the `/ActualText`. A Figure/Formula is "described" iff
 * this is non-empty — the "No description" / "No alt text" badge keys on this,
 * NOT on `alt` alone. Before this, a Figure/Formula described only via
 * `/ActualText` (which the remediator commonly writes) was falsely flagged
 * undescribed (RCA pdf-remediation-quality-regression-260901 §2.2 / §3.1).
 */
export function cardEffectiveDescription(card) {
    const alt = (card.alt ?? "").trim();
    if (alt !== "")
        return alt;
    return (card.actualText ?? "").trim();
}
/**
 * Per-role label + placeholder for the description textarea. Mirrors the
 * mockup vocabulary (`tmp/editor-mockup-card-gui-260501.html` §`DESC_LABELS`).
 * Generic "Description" was confusing — a Formula has a very different
 * writing prompt than alt text on a static figure.
 */
// WHY: Readonly<Record<string, ...>> annotation is intentional — keyed by
// card.role: string at runtime. The fallback `|| {...}` at the callsite handles
// missing keys; this is a per-role lookup table that may receive unknown roles.
export const DESCRIPTION_FIELD_COPY = Object.freeze({
    Figure: { label: "Alt text", placeholder: "Describe what this figure shows for screen reader users…" },
    Table: { label: "Table summary", placeholder: "Summarise the table's purpose and key takeaway…" },
    Formula: { label: "Formula description", placeholder: "Describe the formula in plain language (e.g. 'Mass–energy equivalence: energy equals mass times the speed of light squared')…" },
});
/** Lookup: role token → entry in ROLE_TREE. */
const ROLE_BY_TOKEN = (() => {
    const m = new Map();
    for (const sec of ROLE_TREE)
        for (const it of sec.items)
            m.set(it.role, it);
    return m;
})();
// ─── blockToCard ────────────────────────────────────────────────────────────
/**
 * Convert an IR block to a UI card descriptor used by render functions.
 *
 * @param {object} block  IR block (Heading / Paragraph / Figure / etc.)
 * @returns {{
 *   structId: string,
 *   role: string,        // e.g. "H1", "Paragraph", "Figure"
 *   roleToken: string,   // role used by the type popover (one of ROLE_TREE)
 *   level: number|null,
 *   alt: string,
 *   decorative: boolean,
 *   text: string,        // preview text, plain (no inline structure)
 *   page: number,
 *   bbox: {x:number,y:number,w:number,h:number}|null,
 *   readingOrderIndex: number|null,
 * }}
 */
export function blockToCard(block) {
    if (!block)
        return null;
    // Newly-inserted client-side blocks (handleNewInsert in editor.js) carry
    // `role: ROLE_UNDEFINED` directly with no `kind`. Honor it before falling
    // back to the kind-derived path so UNDEFINED cards render correctly.
    if (block.role === ROLE_UNDEFINED || block.kind === ROLE_UNDEFINED) {
        return {
            structId: block.structId ?? "",
            role: ROLE_UNDEFINED,
            roleToken: ROLE_UNDEFINED,
            level: null,
            alt: block.alt ?? "",
            actualText: block.actualText ?? "",
            decorative: false,
            text: "",
            page: block.page ?? 1,
            bbox: block.bbox ?? null,
            readingOrderIndex: typeof block.readingOrderIndex === "number" ? block.readingOrderIndex : null,
            issue: null,
        };
    }
    const role = block.kind === "Heading"
        ? `H${block.level ?? 1}`
        : (block.kind === "Paragraph" ? "Paragraph" : (block.kind ?? ""));
    const roleToken = block.kind === "Heading"
        ? `H${block.level ?? 1}`
        : (block.kind === "Paragraph" ? "P" : (block.kind ?? ""));
    return {
        structId: block.structId ?? "",
        role,
        roleToken,
        level: block.kind === "Heading" ? (block.level ?? 1) : null,
        alt: block.alt ?? "",
        actualText: block.actualText ?? "",
        decorative: !!block.decorative,
        text: extractPreviewText(block),
        page: block.page ?? 1,
        bbox: block.bbox ?? null,
        readingOrderIndex: typeof block.readingOrderIndex === "number" ? block.readingOrderIndex : null,
        // Forward the server-side issue tag (omitted on the wire when
        // null; treat as null on the card).
        issue: typeof block.issue === "string" && block.issue.length > 0 ? block.issue : null,
        // List item previews (#9) — populated for List blocks only. Omitted
        // (not set to undefined) for non-List blocks so the optional `items?`
        // property holds under exactOptionalPropertyTypes.
        ...(block.kind === "List" ? { items: listItemPreviews(block) } : {}),
    };
}
/**
 * Stable display copy for a structural-issue tag. Mirrors the C# enum
 * of allowed `EditorIRBlock.Issue` values (see EditorIRDocument.cs).
 * Adding a new issue here MUST be paired with a server-side detector
 * that emits that tag.
 *
 * @param {string} issue  issue tag, e.g. "loose-caption"
 * @returns {{ label: string, message: string, fixToRole: string|null }|null}
 *   - label: short text shown in the issue badge
 *   - message: explanatory copy shown when the badge is clicked
 *   - fixToRole: role token to apply via the quick-fix action, or null
 *     if no automatic fix is offered
 */
export function describeIssue(issue) {
    if (issue === "loose-caption") {
        return {
            label: "⚠ Loose caption",
            message: "This caption isn't adjacent to a Figure or Table — assistive " +
                "technologies won't connect them. Drag the caption next to a " +
                "Figure/Table or change the role to Paragraph.",
            fixToRole: "P",
        };
    }
    return null;
}
/**
 * Extract a one-line preview string from an IR block. For text-bearing
 * blocks, this concatenates Inline content. For Figure/Formula/Table,
 * returns a sensible placeholder. Truncated by the renderer; we return
 * full text here so the expanded state can show more.
 */
export function extractPreviewText(block) {
    if (!block)
        return "";
    if (block.kind === "Figure") {
        // Prefer /Alt; fall back to /ActualText so a figure described only via
        // /ActualText shows its description instead of an empty preview.
        return (block.alt && block.alt.trim() !== "")
            ? block.alt
            : (block.actualText ?? "");
    }
    if (block.kind === "Formula") {
        // latex already carries /ActualText when present (ExtractText prefers it);
        // fall back to alt/actualText so a described formula shows its text.
        if (block.latex && block.latex.trim() !== "")
            return block.latex;
        if (block.alt && block.alt.trim() !== "")
            return block.alt;
        return block.actualText ?? "";
    }
    if (block.kind === "Artifact") {
        return block.raw ?? "";
    }
    if (block.kind === "Table") {
        return `Table — ${(block.rows ?? []).length} rows`;
    }
    if (block.kind === "List") {
        return `List — ${(block.items ?? []).length} items`;
    }
    if (Array.isArray(block.content)) {
        return inlineText(block.content);
    }
    return "";
}
/**
 * Extract a per-item preview for each ListItem of a `List` block: the LI's
 * `structId` paired with its flattened text. Each item's `content` is a
 * Block[]; we join each content block's preview text so an expanded List card
 * can show what the items say AND address per-item edit ops by structId.
 * Returns [] for a non-List / itemless block.
 */
export function listItemPreviews(block) {
    if (!block || !Array.isArray(block.items))
        return [];
    const previews = [];
    for (const item of block.items) {
        const structId = item.structId ?? "";
        const content = item.content;
        const contentBlocks = Array.isArray(content) ? content : [];
        const text = contentBlocks
            .map((cb) => extractPreviewText(cb))
            .filter((s) => !!s)
            .join(" ");
        // INV-LI-1 (editor-ux-correctness-261007 bug 3): omit an empty-noise LI
        // — no preview text AND no page geometry — so it never surfaces as a
        // spurious blank row in the expanded List card. An empty-noise /LI can
        // survive the producer's top-level-only drop (IsEmptyStructureNoiseLeaf
        // runs on top-level blocks, not nested LIs). KEEP an item that has
        // geometry (its own bbox or any content block's bbox) even when its text
        // is empty — a tagged region the user must see to re-tag/describe, the
        // same fidelity guard isEmptyStructureNoiseBlock applies to top-level
        // blocks.
        const itemBBox = item.bbox;
        const hasGeometry = (itemBBox !== null && itemBBox !== undefined) ||
            contentBlocks.some((cb) => {
                const b = cb;
                return b != null && b.bbox !== null && b.bbox !== undefined;
            });
        if (text.trim() === "" && !hasGeometry) {
            continue;
        }
        previews.push({ structId, text });
    }
    return previews;
}
function inlineText(inlines) {
    if (!Array.isArray(inlines))
        return "";
    return inlines.map((i) => {
        if (!i || typeof i !== "object")
            return "";
        const inl = i;
        if (inl.kind === "Text")
            return inl.value || "";
        if (inl.kind === "Run")
            return inlineText(inl.content || []);
        return "";
    }).join("");
}
/**
 * Display-side guard for the empty-structure-leaf class
 * (docable-editor-liststuck-emptypara-261001 bug B, part 2). True when a
 * Paragraph/Span block carries NO preview text AND NO page geometry
 * (null/absent `bbox`) — pure structural noise with nothing to render and no
 * overlay to address. The producer (`PdfStructTree.ToEditorIR`'s
 * `IsEmptyStructureNoiseLeaf`) now drops these, but the card builder guards
 * too so a slipped-through noise block never surfaces as a broken "(empty)"
 * card or stalls the "Building item list" state.
 *
 * Fidelity guard — mirror of the C# rule: a block with real geometry
 * (non-null `bbox`) is KEPT even when its text is empty. That is a
 * remediation-side "tagged region, no text" artifact the user must see to
 * re-tag/describe, not editor noise.
 */
export function isEmptyStructureNoiseBlock(block) {
    if (!block)
        return false;
    if (block.kind !== "Paragraph" && block.kind !== "Span")
        return false;
    const hasBBox = block.bbox !== null && block.bbox !== undefined;
    if (hasBBox)
        return false;
    return extractPreviewText(block).trim() === "";
}
/**
 * Build a Card[] from an IR Document. Top-level blocks only.
 *
 * Reading-order index is the SERVER's `readingOrderIndex` (document-wide,
 * struct order; stamped by the C# producer on every block —
 * EditorIRDocument.cs). `buildCards` PRESERVES it (editor-ux-correctness-261007
 * bug 1 / INV-ORD-2); it previously OVERWROTE it with the raw array position,
 * discarding the server value, so `renderCardList`'s page grouping saw a
 * struct-tree that visited pages 2,1,2,3,1 and emitted that exact (wrong)
 * header sequence. The array index is used only as a fallback when a block
 * carries no server value (degraded `struct-tree-fallback` payload; see §G-3).
 * A `reorder-reading` op re-derives the order at op time (recomputed by the
 * host's reorder path, not here), so the preserved value never goes stale in
 * practice.
 *
 * @param {object} ir  IR Document
 * @returns {Array} Card[]
 */
export function buildCards(ir) {
    if (!ir || !Array.isArray(ir.blocks))
        return [];
    const cards = [];
    ir.blocks.forEach((b, i) => {
        let c = null;
        try {
            // Display-side defense-in-depth for the empty-structure-leaf class
            // (docable-editor-liststuck-emptypara-261001 bug B, part 2 —
            // "shouldn't VISUALIZE it even if we did"). The producer
            // (`ToEditorIR`) now drops pure-noise empty leaves, but if one ever
            // slips through (a future producer path, a stale cached IR) the card
            // list must NOT render it as a broken "(empty)" card. Skip it — the
            // same way a null/rejected entry is skipped (it consumes its array
            // index, producing no card), so the surviving cards' reading-order
            // semantics are unchanged. Inside the try so a block whose property
            // access throws still routes to the CRP-4 error card below.
            if (isEmptyStructureNoiseBlock(b)) {
                return;
            }
            c = blockToCard(b);
        }
        catch (err) {
            // CRP-4 (design §H.2, editor-create-role-parity): a single malformed
            // block must NOT blank the whole batch. Without this isolation one
            // throwing block collapses the entire `.map` → every card falls to the
            // roleAbbrev "?" fallback (the reported "all badges = '?'" symptom).
            // Convert the failure into ONE error card and keep going.
            log.warn("[editor:cards] blockToCard threw — isolating to a single error card (CRP-4)", { index: i, err });
            c = makeErrorCard(b, i);
        }
        if (c) {
            // INV-ORD-2: keep the server's readingOrderIndex (blockToCard
            // already copied it from the block). Fall back to the 1-based array
            // position only when the block carried no server value (degraded
            // payload). Do NOT clobber the server value with `i + 1`.
            if (typeof c.readingOrderIndex !== "number") {
                c.readingOrderIndex = i + 1;
            }
            cards.push(c);
        }
    });
    return cards;
}
/**
 * Fallback Card for a block whose `blockToCard` construction threw (CRP-4). The
 * `"?"` role deliberately abbreviates to the badge "?" (roleAbbrev), scoping the
 * blast radius to this one card while its siblings render normally.
 */
function makeErrorCard(block, index) {
    const b = (block && typeof block === "object") ? block : null;
    const structId = (b && typeof b.structId === "string" && b.structId.length > 0)
        ? b.structId
        : `__unrenderable_${index}`;
    return {
        structId,
        role: "?",
        roleToken: "?",
        level: null,
        alt: "",
        actualText: "",
        decorative: false,
        text: "This element could not be displayed.",
        page: (b && typeof b.page === "number") ? b.page : 1,
        bbox: null,
        readingOrderIndex: null,
        issue: null,
    };
}
export function renderCardList(opts) {
    const { container, cards, expandedIds, selectedIds, onSelect, onOp, onExpand, onCollapse, onReorder, onReorderClick, onJumpToPage, getSelectedIds, // Live accessor so the drag handler can honor the
    // current selection at drop-time, not at render-
    // time. Optional; falls back to selectedIds.
     } = opts;
    if (!container)
        return;
    container.innerHTML = "";
    container.classList.add("editor-card-list");
    if (!cards || cards.length === 0) {
        const p = document.createElement("p"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        p.className = "editor-cards-empty";
        p.textContent = "No accessibility structure elements found.";
        container.appendChild(p);
        return;
    }
    const isMulti = selectedIds && selectedIds.size > 1;
    const frag = document.createDocumentFragment(); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    // Optional page grouping: emit a sticky `.editor-card-page-header`
    // before the first card on each new page.
    const groupByPage = typeof onJumpToPage === "function";
    // INV-ORD-1 (editor-ux-correctness-261007 bug 1): when grouping by page,
    // SORT the cards by (page, server readingOrderIndex) before emitting headers
    // so the header sequence is strictly non-decreasing in page order and
    // intra-page cards are in ascending logical reading order. The IR arrives in
    // raw struct-tree order (which may visit pages 2,1,2,3,1); the pre-fix code
    // only tracked the previous page number, so it emitted a header on every
    // page-run CHANGE — reproducing that jumping sequence. §G-3 ratified
    // fallback: when a card has no server readingOrderIndex (degraded payload),
    // fall back to (page, array-order), preserving producer order within a page.
    // The flat (ungrouped) path keeps document order for back-compat.
    const orderedCards = groupByPage
        ? cards
            .map((c, idx) => ({ c, idx }))
            .sort((a, b) => {
            const pa = a.c.page || 1;
            const pb = b.c.page || 1;
            if (pa !== pb)
                return pa - pb;
            const ra = typeof a.c.readingOrderIndex === "number" ? a.c.readingOrderIndex : a.idx + 1;
            const rb = typeof b.c.readingOrderIndex === "number" ? b.c.readingOrderIndex : b.idx + 1;
            if (ra !== rb)
                return ra - rb;
            return a.idx - b.idx; // stable tiebreak (equal keys keep array order)
        })
            .map((x) => x.c)
        : cards;
    let lastPage = -1;
    let pageBuckets = null; // { pageNum: [cards] } for header counts
    if (groupByPage) {
        pageBuckets = new Map();
        for (const c of orderedCards) {
            const p = c.page || 1;
            if (!pageBuckets.has(p))
                pageBuckets.set(p, []);
            const bucket = pageBuckets.get(p);
            if (bucket)
                bucket.push(c);
        }
    }
    for (const card of orderedCards) {
        if (groupByPage && card.page !== lastPage) {
            lastPage = card.page;
            const header = makePageSectionHeader({
                pageNum: card.page,
                count: (pageBuckets?.get(card.page) ?? []).length,
                onJumpToPage: onJumpToPage,
            });
            frag.appendChild(header);
        }
        const isSel = selectedIds?.has(card.structId);
        let el;
        try {
            el = createCardEl({
                card,
                isExpanded: expandedIds?.has(card.structId),
                isSelected: isSel,
                isMultiSelected: isSel && isMulti,
                onSelect, onOp, onExpand, onCollapse, onJumpToPage,
                onReorderClick,
            });
        }
        catch (err) {
            // CRP-4 (design §H.2): the DOM-construction leg of the same defense.
            // A card whose element build throws renders as a single error card so
            // the batch — and every sibling badge — still renders.
            log.warn("[editor:cards] createCardEl threw — rendering a single error card (CRP-4)", { structId: card?.structId, err });
            el = makeErrorCardEl(card);
        }
        frag.appendChild(el);
    }
    container.appendChild(frag);
    if (typeof onReorder === "function") {
        // Drag-and-drop must reason over the SAME order the cards render in
        // (orderedCards), so a drop computes neighbours against what the user
        // sees, not the raw struct-tree order.
        wireDragAndDrop(container, orderedCards, onReorder, getSelectedIds ?? (() => selectedIds ?? new Set()));
    }
}
function makePageSectionHeader({ pageNum, count, onJumpToPage }) {
    const header = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    header.className = "editor-card-page-header";
    header.dataset.page = String(pageNum);
    const label = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    label.className = "editor-card-page-header-label";
    label.textContent = `Page ${pageNum}  ·  ${count} element${count === 1 ? "" : "s"}`;
    header.appendChild(label);
    const btn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    btn.type = "button";
    btn.className = "editor-card-page-header-jump";
    btn.textContent = "Scroll to page ↑";
    btn.title = `Scroll the PDF preview to page ${pageNum}`;
    btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        onJumpToPage(pageNum);
    });
    header.appendChild(btn);
    return header;
}
/**
 * Minimal error-card element for a card whose `createCardEl` build threw (CRP-4,
 * design §H.2). Deliberately construction-trivial — no popover, no handlers — so
 * it cannot itself throw and re-widen the blast radius. Shows the "?" badge and a
 * short message; the loop continues rendering every other card.
 */
function makeErrorCardEl(card) {
    const el = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    el.className = "editor-card editor-card-error";
    el.dataset.structId = card?.structId ?? "";
    el.dataset.type = "?";
    el.dataset.roleToken = "?";
    const badge = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    badge.className = "editor-card-role-badge";
    badge.textContent = "?";
    el.appendChild(badge);
    const msg = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    msg.className = "editor-card-error-msg";
    msg.textContent = "This element could not be displayed.";
    el.appendChild(msg);
    return el;
}
export function createCardEl(opts) {
    const { card, isExpanded, isSelected, isMultiSelected, onSelect, onOp, onExpand, onCollapse, onJumpToPage, onReorderClick } = opts;
    const el = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    el.className = "editor-card";
    el.dataset.structId = card.structId;
    el.dataset.type = card.role;
    el.dataset.roleToken = card.roleToken;
    el.dataset.page = String(card.page);
    el.draggable = true;
    if (isExpanded)
        el.classList.add("is-expanded");
    if (isSelected)
        el.classList.add("is-selected");
    if (isMultiSelected)
        el.classList.add("is-multi-selected");
    // UNDEFINED card — newly inserted, awaiting type. Pulsing yellow
    // border + caution prompt mirrors the matching overlay treatment so
    // the unresolved insertion is unmissable. Mockup ref: card-gui
    // §.el-card.is-undefined + §.undef-prompt.
    const isUndefined = card.role === ROLE_UNDEFINED || card.roleToken === ROLE_UNDEFINED;
    if (isUndefined)
        el.classList.add("is-undefined");
    // 1) drag handle (left strip)
    const handle = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    handle.className = "editor-card-handle";
    handle.title = "Drag to reorder";
    handle.setAttribute("aria-hidden", "true");
    handle.textContent = "⠇⠇"; // dotted gripper
    el.appendChild(handle);
    // 2) thin colored type bar
    const bar = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    bar.className = "editor-card-typebar";
    el.appendChild(bar);
    // 3) card body
    const body = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    body.className = "editor-card-body";
    el.appendChild(body);
    // top row: type chip + (optional) issue badge + jump btn
    const topRow = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    topRow.className = "editor-card-toprow";
    body.appendChild(topRow);
    const chip = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    chip.type = "button";
    chip.className = "editor-card-typechip";
    chip.dataset.role = card.roleToken;
    chip.textContent = isUndefined ? ROLE_UNDEFINED : card.role;
    chip.setAttribute("aria-haspopup", "true");
    chip.setAttribute("aria-label", isUndefined
        ? "Choose element type"
        : `Change type — currently ${card.role}`);
    // Hover tooltip — what does this role mean? Sourced from ROLE_TREE so
    // the popover description and the hover hint never diverge. Mockup
    // ref: tmp/editor-mockup-error-friendly-260501.html §role-chip[data-tooltip].
    if (isUndefined) {
        chip.title = "Click to choose a structural role for this newly-inserted element.";
    }
    else {
        const entry = ROLE_BY_TOKEN.get(card.roleToken);
        if (entry?.desc)
            chip.title = `${entry.label} — ${entry.desc}`;
    }
    chip.addEventListener("click", (ev) => {
        ev.stopPropagation();
        openTypePopover({
            anchor: chip,
            currentRole: card.roleToken,
            onPick: (newRole) => {
                if (newRole === card.roleToken)
                    return;
                if (typeof onOp === "function") {
                    onOp({ kind: "set-role", structId: card.structId, before: card.roleToken, after: newRole });
                }
            },
        });
    });
    topRow.appendChild(chip);
    // Reading-order chip: numeric label flanked by ▲/▼ arrow buttons.
    // Sits IMMEDIATELY to the right of the type chip so the user finds
    // type and order controls clustered in the upper-left of the card —
    // mirrors the overlay-side `.editor-overlay-controlbar` placement.
    // No-op when `onReorderClick` isn't supplied (back-compat with the
    // few unit tests that render cards without a host).
    if (typeof onReorderClick === "function" && Number.isFinite(card.readingOrderIndex)) {
        const orderChip = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        orderChip.className = "editor-card-order-chip";
        orderChip.dataset.order = String(card.readingOrderIndex);
        orderChip.setAttribute("aria-label", `Reading order ${card.readingOrderIndex}`);
        const upBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        upBtn.type = "button";
        upBtn.className = "editor-card-order-arrow up";
        upBtn.textContent = "▲";
        upBtn.title = "Move earlier in reading order";
        upBtn.setAttribute("aria-label", "▲ Move earlier in reading order");
        upBtn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            onReorderClick(card.structId, "up");
        });
        orderChip.appendChild(upBtn);
        const numEl = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        numEl.className = "editor-card-order-num";
        numEl.textContent = String(card.readingOrderIndex);
        orderChip.appendChild(numEl);
        const dnBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        dnBtn.type = "button";
        dnBtn.className = "editor-card-order-arrow down";
        dnBtn.textContent = "▼";
        dnBtn.title = "Move later in reading order";
        dnBtn.setAttribute("aria-label", "▼ Move later in reading order");
        dnBtn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            onReorderClick(card.structId, "down");
        });
        orderChip.appendChild(dnBtn);
        topRow.appendChild(orderChip);
    }
    // Issue badge: UNDEFINED type required. Same shape as the other issue
    // badges so the user reaches for the same affordance regardless of
    // which kind of issue it is.
    if (isUndefined) {
        const badge = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        badge.type = "button";
        badge.className = "editor-card-issue-badge";
        badge.textContent = "⚠ Type required";
        badge.title = "Choose a structural role for this newly-inserted element";
        badge.addEventListener("click", (ev) => {
            ev.stopPropagation();
            openTypePopover({
                anchor: chip,
                currentRole: card.roleToken,
                onPick: (newRole) => {
                    if (typeof onOp === "function") {
                        onOp({ kind: "set-role", structId: card.structId, before: card.roleToken, after: newRole });
                    }
                },
            });
        });
        topRow.appendChild(badge);
    }
    // Issue badge: any description-bearing role with no description text
    // (Figure / Table / Formula — the DESCRIPTION_ROLES set). Mockup ref:
    // card-gui §card-p3-audio "⚠ No description". Wording differs per role —
    // Figure says "No alt text"; the others say "No description".
    if (DESCRIPTION_ROLES.has(card.role) && !card.decorative
        && cardEffectiveDescription(card) === "") {
        const badge = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        badge.type = "button";
        badge.className = "editor-card-issue-badge";
        const label = card.role === "Figure" ? "⚠ No alt text" : "⚠ No description";
        badge.textContent = label;
        badge.title = card.role === "Figure"
            ? "Click to add alt text"
            : `Click to add ${card.role.toLowerCase()} description`;
        badge.addEventListener("click", (ev) => {
            ev.stopPropagation();
            if (typeof onExpand === "function")
                onExpand(card.structId);
            // After expansion, attempt to focus the description textarea on
            // the re-rendered card (host re-renders after expand).
            setTimeout(() => {
                const tx = document.querySelector(// noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                `.editor-card[data-struct-id="${cssEscape(card.structId)}"] .editor-card-alt-text`);
                if (tx && typeof tx.focus === "function")
                    tx.focus();
            }, 0);
        });
        topRow.appendChild(badge);
    }
    // Issue badge: server-side structural-defect tag (e.g. loose-caption)
    const issueDesc = describeIssue(card.issue);
    if (issueDesc) {
        const badge = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        badge.type = "button";
        badge.className = "editor-card-issue-badge";
        badge.dataset.issue = card.issue ?? "";
        badge.textContent = issueDesc.label;
        badge.title = issueDesc.message;
        badge.addEventListener("click", (ev) => {
            ev.stopPropagation();
            // Open the explanation + quick-fix in a small popover.
            openIssuePopover({ anchor: badge, card, issueDesc, onOp });
        });
        topRow.appendChild(badge);
    }
    // Disclosure caret (bug 7 / INV-CLICK-2, editor-ux-correctness-261007 §G-1):
    // card expand is reachable ONLY through this explicit control. The ↗ jump
    // button was removed — a single click anywhere else on the card is now the
    // strong jump (see the card-level click handler below), never expand.
    if (!isUndefined && (typeof onExpand === "function" || typeof onCollapse === "function")) {
        const caret = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        caret.type = "button";
        caret.className = "editor-card-expand-toggle";
        caret.setAttribute("aria-expanded", isExpanded ? "true" : "false");
        caret.setAttribute("aria-label", isExpanded ? "Collapse details" : "Expand details");
        caret.title = isExpanded ? "Collapse" : "Expand to edit";
        // Decorative glyph: aria-hidden so the accessible name is solely the
        // aria-label (WCAG 2.5.3 label-in-name).
        const caretIcon = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        caretIcon.setAttribute("aria-hidden", "true");
        caretIcon.textContent = isExpanded ? "▾" : "▸";
        caret.appendChild(caretIcon);
        caret.addEventListener("click", (ev) => {
            ev.stopPropagation();
            if (isExpanded) {
                if (typeof onCollapse === "function")
                    onCollapse(card.structId);
            }
            else if (typeof onExpand === "function") {
                onExpand(card.structId);
            }
        });
        topRow.appendChild(caret);
    }
    // body content: undef-prompt OR compact preview OR expanded fields.
    // UNDEFINED takes priority — the user cannot do anything useful with
    // a typeless card, so we replace the preview with a "Choose type" CTA.
    // Mockup ref: card-gui §.undef-prompt + §undef-choose-btn.
    if (isUndefined) {
        const prompt = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        prompt.className = "editor-card-undef-prompt";
        const icon = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        icon.className = "editor-card-undef-prompt-icon";
        icon.setAttribute("aria-hidden", "true");
        icon.textContent = "?";
        prompt.appendChild(icon);
        const text = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        text.className = "editor-card-undef-prompt-text";
        text.textContent = "Choose a type for this element";
        prompt.appendChild(text);
        const chooseBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        chooseBtn.type = "button";
        chooseBtn.className = "editor-card-undef-choose-btn";
        chooseBtn.textContent = "Choose type";
        chooseBtn.title = "Open the structural-role picker";
        chooseBtn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            openTypePopover({
                anchor: chooseBtn,
                currentRole: card.roleToken,
                onPick: (newRole) => {
                    if (typeof onOp === "function") {
                        onOp({ kind: "set-role", structId: card.structId, before: card.roleToken, after: newRole });
                    }
                },
            });
        });
        prompt.appendChild(chooseBtn);
        body.appendChild(prompt);
    }
    else if (isExpanded) {
        body.appendChild(buildExpandedFields({ card, onOp, onCollapse }));
    }
    else {
        const preview = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        preview.className = "editor-card-preview";
        const text = card.text || "";
        const isEmptyAlt = card.role === "Figure" && !card.decorative
            && cardEffectiveDescription(card) === "";
        if (isEmptyAlt) {
            preview.classList.add("is-empty-alt");
            // Click now jumps (INV-CLICK-1); the "⚠ No alt text" badge + the
            // disclosure caret are the add-alt affordances, so don't tell the
            // user to "click to add" here.
            preview.textContent = "(no alt text)";
        }
        else {
            preview.textContent = truncate(text, 140) || "(empty)";
        }
        // No click handler on the preview — a click here falls through to the
        // card-level strong-jump handler (bug 7 / INV-CLICK-1). Expand is the
        // disclosure caret's job only (INV-CLICK-2).
        body.appendChild(preview);
    }
    // Strong jump (bug 7 / INV-CLICK-1, editor-ux-correctness-261007 §G-1): a
    // single click anywhere on the card — the preview included — selects it AND
    // navigates the PDF preview to its page. onSelect does the select + flash
    // (and, in single mode, the host's overlay scroll); onJumpToPage routes the
    // page scroll through the host's awaited multiPageMgr.scrollToPage, so the
    // jump lands even on a not-yet-rendered (lazy) page. Modifier-held clicks
    // (shift/cmd/ctrl) are a multi-select gesture — extend the selection only,
    // never yank the viewport.
    const activateCard = (ev) => {
        if (typeof onSelect === "function")
            onSelect(card.structId, ev);
        if (modeFromEvent(ev) === "single" && typeof onJumpToPage === "function") {
            onJumpToPage(card.page);
        }
    };
    el.addEventListener("click", (ev) => {
        // Interactive descendants own their own click — don't double-handle.
        const t = ev.target;
        if (t.closest(".editor-card-typechip"))
            return;
        if (t.closest(".editor-card-expand-toggle"))
            return;
        if (t.closest(".editor-card-issue-badge"))
            return;
        if (t.closest(".editor-card-handle"))
            return;
        if (t.closest(".editor-card-fields"))
            return;
        activateCard(ev);
    });
    // Keyboard activation (a11y — the removed ↗ button was the card's only
    // focusable jump affordance, so keep the card itself keyboard-activatable
    // per §G-1). Enter on the focused card fires the same strong jump as a
    // single click. Guarded to the card element itself so Enter inside a field
    // or child button is never hijacked.
    el.tabIndex = 0;
    el.addEventListener("keydown", (ev) => {
        if (ev.key !== "Enter")
            return;
        if (ev.target !== el)
            return;
        ev.preventDefault();
        activateCard(ev);
    });
    return el;
}
/** Build the expanded-state fields container based on role. */
function buildExpandedFields({ card, onOp, onCollapse }) {
    const fields = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    fields.className = "editor-card-fields";
    if (DESCRIPTION_ROLES.has(card.role) || card.role === "Figure") {
        // Per-role copy keeps the field label aligned with the writing
        // prompt (alt text vs audio/video/formula description vs table
        // summary). Falls back to the generic shape for any future role
        // we add to DESCRIPTION_ROLES before adding copy here.
        const copy = DESCRIPTION_FIELD_COPY[card.role] ?? {
            label: "Description",
            placeholder: "Describe this " + card.role.toLowerCase() + " for screen-reader users…",
        };
        const label = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        label.className = "editor-card-field-label";
        label.textContent = copy.label;
        fields.appendChild(label);
        const tx = document.createElement("textarea"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        tx.className = "editor-card-alt-text";
        tx.placeholder = copy.placeholder;
        tx.value = card.alt || "";
        autoGrowTextarea(tx, { maxFraction: 0.1 });
        tx.addEventListener("input", debounce(() => {
            if (typeof onOp === "function") {
                onOp({
                    kind: "set-alt-text",
                    structId: card.structId,
                    before: card.alt || "",
                    after: tx.value,
                });
            }
        }, 200));
        fields.appendChild(tx);
        if (card.role === "Figure") {
            const decRow = document.createElement("label"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            decRow.className = "editor-card-decorative-row";
            const cb = document.createElement("input"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            cb.type = "checkbox";
            cb.checked = !!card.decorative;
            cb.addEventListener("change", () => {
                if (typeof onOp === "function") {
                    onOp({
                        kind: "set-decorative",
                        structId: card.structId,
                        before: !!card.decorative,
                        after: cb.checked,
                    });
                }
            });
            decRow.appendChild(cb);
            const dt = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            dt.textContent = " Mark as decorative (skip alt text)";
            decRow.appendChild(dt);
            fields.appendChild(decRow);
        }
    }
    else if (card.role === "Paragraph") {
        const label = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        label.className = "editor-card-field-label";
        label.textContent = "Body text";
        fields.appendChild(label);
        // Editable. The IR's `set-actual-text` op writes the override into
        // the struct elem's /ActualText attribute on Save (canonical PDF/UA
        // mechanism: screen readers speak /ActualText instead of the
        // MCID-extracted glyphs). Useful for OCR fixups, ligature breaks,
        // and TJ-positioned text that extracts without spaces.
        const tx = document.createElement("textarea"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        tx.className = "editor-card-text";
        tx.value = card.text || "";
        tx.placeholder = "Type to override the screen-reader reading for this paragraph";
        autoGrowTextarea(tx, { maxFraction: 0.1 });
        tx.addEventListener("input", debounce(() => {
            if (typeof onOp !== "function")
                return;
            const newVal = tx.value;
            const before = card.text || "";
            if (newVal === before)
                return;
            onOp({
                kind: "set-actual-text",
                structId: card.structId,
                before,
                after: newVal,
            });
        }, 200));
        fields.appendChild(tx);
        const note = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        note.className = "editor-card-para-edit-note";
        note.textContent = "Edits override the screen-reader text (writes to PDF /ActualText on Save).";
        fields.appendChild(note);
    }
    else if (card.role.startsWith("H")) {
        const label = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        label.className = "editor-card-field-label";
        label.textContent = "Heading text";
        fields.appendChild(label);
        const inp = document.createElement("input"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        inp.type = "text";
        inp.className = "editor-card-text";
        inp.value = card.text || "";
        inp.addEventListener("input", debounce(() => {
            if (typeof onOp === "function") {
                onOp({
                    kind: "set-actual-text",
                    structId: card.structId,
                    after: inp.value,
                });
            }
        }, 200));
        fields.appendChild(inp);
    }
    else if (card.role === "Caption" || card.role === "Note" || card.role === "Span") {
        const label = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        label.className = "editor-card-field-label";
        label.textContent = `${card.role} text`;
        fields.appendChild(label);
        const inp = document.createElement("input"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        inp.type = "text";
        inp.className = "editor-card-text";
        inp.value = card.text || "";
        inp.addEventListener("input", debounce(() => {
            if (typeof onOp === "function") {
                onOp({
                    kind: "set-actual-text",
                    structId: card.structId,
                    after: inp.value,
                });
            }
        }, 200));
        fields.appendChild(inp);
    }
    else if (card.role === "Link") {
        // Link card — link text is the primary input. URL is editable but
        // typically already correct (we read it from the PDF). Description
        // is OPTIONAL: most links use the link text itself, so we hide the
        // description block behind an "+ Add description" toggle. Mockup ref:
        // tmp/editor-mockup-card-gui-260501.html §card-p3-link2 +
        // §toggleLinkDescription.
        const linkLbl = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        linkLbl.className = "editor-card-field-label";
        linkLbl.textContent = "Link text";
        fields.appendChild(linkLbl);
        const linkText = document.createElement("input"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        linkText.type = "text";
        linkText.className = "editor-card-text";
        linkText.value = card.text || "";
        linkText.addEventListener("input", debounce(() => {
            if (typeof onOp === "function") {
                onOp({
                    kind: "set-actual-text",
                    structId: card.structId,
                    after: linkText.value,
                });
            }
        }, 200));
        fields.appendChild(linkText);
        // The optional-description block is initially hidden behind a
        // toggle. If the card already has a non-empty description (.alt)
        // we open it by default so the user can see / edit existing copy.
        const hasExistingDesc = (card.alt || "").trim().length > 0;
        const descBlock = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        descBlock.className = "editor-card-link-desc-block";
        if (!hasExistingDesc)
            descBlock.style.display = "none";
        const descLbl = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        descLbl.className = "editor-card-field-label";
        descLbl.textContent = "Description (optional)";
        descBlock.appendChild(descLbl);
        const descTa = document.createElement("textarea"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        descTa.className = "editor-card-alt-text";
        descTa.placeholder = "Add a description for users when the link text isn't clear (e.g. 'Open accessibility report PDF').";
        descTa.value = card.alt || "";
        autoGrowTextarea(descTa, { maxFraction: 0.1 });
        descTa.addEventListener("input", debounce(() => {
            if (typeof onOp === "function") {
                onOp({
                    kind: "set-alt-text",
                    structId: card.structId,
                    before: card.alt || "",
                    after: descTa.value,
                });
            }
        }, 200));
        descBlock.appendChild(descTa);
        const addDescBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        addDescBtn.type = "button";
        addDescBtn.className = "editor-card-link-add-desc-btn";
        addDescBtn.textContent = "+ Add description";
        if (hasExistingDesc)
            addDescBtn.style.display = "none";
        addDescBtn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            descBlock.style.display = "";
            addDescBtn.style.display = "none";
            if (typeof descTa.focus === "function") {
                setTimeout(() => descTa.focus(), 0);
            }
        });
        fields.appendChild(addDescBtn);
        fields.appendChild(descBlock);
    }
    else if (card.role === "List") {
        // Editable per-item rows (editor-pdf-item-editing-260910 Phase 4b).
        // Each row carries the LI's structId (from `listItemPreviews`) and
        // dispatches the structId-addressed Phase-4a ops through `onOp`:
        //   • text input      → set-actual-text on the LI structId (writes
        //                        /ActualText on the LI elem on Save)
        //   • ✕ remove        → delete-element (carries parentStructId +
        //                        insertIndex + page so the oplog inverse can
        //                        re-insert the LI — inverseOf in oplog.ts)
        //   • ↑/↓ reorder     → reorder-reading with parentId = the List's
        //                        structId, before/after = the LI structIds
        //   • ＋ add item      → create-element with parentStructId = the
        //                        List's structId, role "LI", insertIndex = tail
        const items = card.items ?? [];
        const currentIds = items.map((it) => it.structId);
        const label = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        label.className = "editor-card-field-label";
        label.textContent = `List items (${items.length})`;
        fields.appendChild(label);
        const list = document.createElement("ul"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        list.className = "editor-card-list-items";
        items.forEach((item, i) => {
            const li = document.createElement("li"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            li.className = "editor-card-list-item";
            if (item.structId)
                li.dataset.itemStructId = item.structId;
            // Editable text — mirrors the Paragraph branch's set-actual-text
            // dispatch. The LI's inner Paragraph carries the LI's own structId
            // in both IR builders, so mutateBlocksById lands the edit on the
            // item and the server writes /ActualText on the LI elem.
            const tx = document.createElement("textarea"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            tx.className = "editor-card-list-item-text";
            tx.value = item.text || "";
            tx.rows = estimateTextareaRows(tx.value, charsPerLineForElement(tx, LIST_ITEM_CHARS_PER_LINE_FALLBACK));
            tx.placeholder = "List item text";
            tx.setAttribute("aria-label", `List item ${i + 1} text`);
            // Un-debounced: resize feels responsive immediately on keystroke.
            tx.addEventListener("input", () => {
                tx.rows = estimateTextareaRows(tx.value, charsPerLineForElement(tx, LIST_ITEM_CHARS_PER_LINE_FALLBACK));
            });
            tx.addEventListener("input", debounce(() => {
                if (typeof onOp !== "function")
                    return;
                const after = tx.value;
                const before = item.text || "";
                if (after === before)
                    return;
                onOp({ kind: "set-actual-text", structId: item.structId, before, after });
            }, 200));
            li.appendChild(tx);
            // ↑ move up — reorder-reading over the List's LI structIds.
            const upBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            upBtn.type = "button";
            upBtn.className = "editor-card-list-item-up";
            upBtn.textContent = "↑";
            upBtn.title = "Move item up";
            upBtn.setAttribute("aria-label", `Move list item ${i + 1} up`);
            upBtn.disabled = i === 0;
            upBtn.addEventListener("click", (ev) => {
                ev.stopPropagation();
                if (typeof onOp !== "function" || i === 0)
                    return;
                const after = currentIds.slice();
                [after[i - 1], after[i]] = [after[i], after[i - 1]];
                onOp({ kind: "reorder-reading", pageNum: card.page, parentId: card.structId, before: currentIds, after });
            });
            li.appendChild(upBtn);
            // ↓ move down — reorder-reading over the List's LI structIds.
            const downBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            downBtn.type = "button";
            downBtn.className = "editor-card-list-item-down";
            downBtn.textContent = "↓";
            downBtn.title = "Move item down";
            downBtn.setAttribute("aria-label", `Move list item ${i + 1} down`);
            downBtn.disabled = i === items.length - 1;
            downBtn.addEventListener("click", (ev) => {
                ev.stopPropagation();
                if (typeof onOp !== "function" || i === items.length - 1)
                    return;
                const after = currentIds.slice();
                [after[i], after[i + 1]] = [after[i + 1], after[i]];
                onOp({ kind: "reorder-reading", pageNum: card.page, parentId: card.structId, before: currentIds, after });
            });
            li.appendChild(downBtn);
            // ✕ remove — delete-element on the LI structId. The parentStructId
            // + insertIndex + page ride along so the oplog inverse (oplog.ts
            // inverseOf) can re-insert this LI on undo.
            const rmBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            rmBtn.type = "button";
            rmBtn.className = "editor-card-list-item-remove";
            rmBtn.textContent = "✕";
            rmBtn.title = "Delete item";
            rmBtn.setAttribute("aria-label", `Delete list item ${i + 1}`);
            rmBtn.addEventListener("click", (ev) => {
                ev.stopPropagation();
                if (typeof onOp !== "function")
                    return;
                onOp({ kind: "delete-element", structId: item.structId, parentStructId: card.structId, insertIndex: i, page: card.page });
            });
            li.appendChild(rmBtn);
            list.appendChild(li);
        });
        fields.appendChild(list);
        // ＋ add item — create-element appends a new empty LI under the List.
        // The client structId is local-only (the server assigns the real
        // path-form id via add-child on Save); the reducer's insertListItem
        // seeds an empty inner Paragraph carrying this id so the row is
        // immediately text-editable.
        const addBtn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        addBtn.type = "button";
        addBtn.className = "editor-card-list-item-add";
        addBtn.textContent = "＋ Add item";
        addBtn.setAttribute("aria-label", "Add a list item");
        addBtn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            if (typeof onOp !== "function")
                return;
            onOp({
                kind: "create-element",
                structId: nextEditorInsertId(),
                parentStructId: card.structId,
                role: "LI",
                bbox: [0, 0, 0, 0],
                page: card.page,
                insertIndex: items.length,
            });
        });
        fields.appendChild(addBtn);
    }
    else {
        const note = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        note.className = "editor-card-field-note";
        note.textContent = "(no editable fields for this type)";
        fields.appendChild(note);
    }
    // Esc collapses
    fields.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape" && typeof onCollapse === "function") {
            ev.stopPropagation();
            onCollapse(card.structId);
        }
    });
    return fields;
}
function truncate(s, n) {
    if (!s)
        return s;
    if (s.length <= n)
        return s;
    return s.slice(0, n - 1).trimEnd() + "…";
}
function debounce(fn, ms) {
    let timer = null;
    return function debounced(...args) {
        if (timer)
            clearTimeout(timer);
        timer = setTimeout(() => { timer = null; fn.apply(this, args); }, ms);
    };
}
/**
 * Estimate how many textarea rows a value needs so the list-item text
 * control (editor-affordance-gaps, 261002) can auto-expand without
 * truncating. Deliberately NOT scrollHeight-based: scrollHeight only
 * reflects reality once the element is attached + laid out (unreliable at
 * the detached-DOM-construction point in buildExpandedFields, and always 0
 * under jsdom), so a length-driven heuristic keeps sizing synchronous,
 * detached-safe, and unit-testable. The clamp bounds runaway height for
 * pathological input; `.editor-card-list-item-text`'s overflow-y:auto in
 * editor.css is the backstop if content still exceeds the clamp.
 */
function estimateTextareaRows(value, charsPerLine = 36, maxRows = 12) {
    if (!value)
        return 1;
    let rows = 0;
    for (const line of value.split("\n")) {
        rows += Math.max(1, Math.ceil(line.length / charsPerLine));
    }
    return Math.min(Math.max(rows, 1), maxRows);
}
/** Approximate glyph width (px) for the editor's 12px sans-serif card-field
 * font — used to turn a measured field width into a chars-per-line count. */
const AVG_CHAR_PX_AT_12PX = 6.5;
/** Floor so a measured-but-tiny width never collapses chars-per-line to an
 * unusably small (or zero) divisor. */
const MIN_CHARS_PER_LINE = 20;
/**
 * Width-aware chars-per-line for `estimateTextareaRows` (editor-listitem-
 * textbox-rows, 261008). RCA a0cc4016: a single fixed `charsPerLine=36` for
 * every card field over-counts rows on a `width:100%` field (the list-item
 * text control wraps at ~55-60 chars/line at 12px, not 36) — long AICM-style
 * item text hit the row cap and rendered a tall box with large empty
 * vertical space below the actual wrapped text. Once attached, `clientWidth`
 * gives the real rendered width; before attach (detached construction in
 * `buildExpandedFields`, or jsdom — where `clientWidth` is always 0 per the
 * `estimateTextareaRows` rationale above) `fallback` stands in, tuned per
 * call site for that field's typical layout width.
 */
function charsPerLineForElement(ta, fallback) {
    const width = ta.clientWidth;
    if (!width)
        return fallback;
    return Math.max(MIN_CHARS_PER_LINE, Math.floor(width / AVG_CHAR_PX_AT_12PX));
}
/** Detached/jsdom chars-per-line fallback for the `width:100%` list-item
 * text control — wider than the narrower alt/description sidebar fields,
 * so it gets its own (larger) fallback rather than sharing the generic 36. */
const LIST_ITEM_CHARS_PER_LINE_FALLBACK = 58;
/**
 * Generous row ceiling for a pane-%-capped auto-grow textarea (bug 8 /
 * INV-GROW-1). Unlike the list-item textarea's fixed 12-row cap, the alt /
 * description / paragraph fields grow until the pane-% `maxHeight` (set in
 * `autoGrowTextarea`) clips them; this ceiling only bounds the row attribute
 * so a pathological value can't request thousands of rows.
 */
const AUTO_GROW_MAX_ROWS = 60;
/**
 * Input-time auto-grow for an editable textarea (bug 8 / INV-GROW-1,
 * editor-ux-correctness-261007). On build and on every `input`, the row count
 * grows with content (length-driven, same detached-safe + jsdom-safe rationale
 * as `estimateTextareaRows` — scrollHeight is 0 under jsdom and unreliable on a
 * detached node), and the rendered height is capped at `maxFraction × the
 * pane's clientHeight` via `maxHeight` + `overflow-y: auto` so a very long value
 * scrolls inside the field instead of pushing sibling fields off-screen.
 *
 * `paneEl` defaults to the nearest resolvable editor pane (`#pane-editor-body`,
 * else the card-list's scroll parent) looked up from the attached textarea — so
 * callers in `buildExpandedFields`, which build detached, need not thread it.
 * Before attach (first `apply`) the pane is unresolvable and the cap is simply
 * deferred to the first real `input`, by which point the field is in the DOM.
 */
export function autoGrowTextarea(ta, opts) {
    const apply = () => {
        ta.rows = estimateTextareaRows(ta.value, charsPerLineForElement(ta, 36), AUTO_GROW_MAX_ROWS);
        const paneEl = opts.paneEl ?? resolveEditorPaneEl(ta);
        const paneH = paneEl ? paneEl.clientHeight : 0;
        if (paneH > 0) {
            ta.style.maxHeight = Math.floor(paneH * opts.maxFraction) + "px";
            ta.style.overflowY = "auto";
        }
    };
    apply();
    ta.addEventListener("input", apply);
}
/** Nearest editor pane whose clientHeight bounds an auto-grow textarea. */
function resolveEditorPaneEl(node) {
    return node.closest("#pane-editor-body")
        ?? node.closest(".editor-card-list")?.parentElement
        ?? null;
}
// ─── Type popover (single source of truth, also reused by ctx menu) ─────────
let _activePopover = null;
export function openTypePopover(opts) {
    closeTypePopover();
    const { anchor, currentRole, onPick } = opts;
    if (!anchor)
        return undefined;
    const pop = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    pop.className = "editor-type-popover is-open";
    pop.setAttribute("role", "menu");
    // search box
    const searchWrap = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    searchWrap.className = "editor-type-popover-search";
    const search = document.createElement("input"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    search.type = "text";
    search.placeholder = "Search roles...";
    search.setAttribute("aria-label", "Search roles");
    searchWrap.appendChild(search);
    pop.appendChild(searchWrap);
    const list = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    list.className = "editor-type-popover-list";
    pop.appendChild(list);
    // Render the role tree
    const allRows = [];
    let firstSection = true;
    for (const sec of ROLE_TREE) {
        const header = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        header.className = "editor-type-section-header" + (firstSection ? " is-first" : "");
        header.textContent = sec.section;
        list.appendChild(header);
        firstSection = false;
        for (const item of sec.items) {
            const row = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            row.type = "button";
            row.className = "editor-type-option" + (item.role === currentRole ? " is-current" : "");
            row.dataset.role = item.role;
            const chip = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            chip.className = "editor-type-option-chip";
            chip.dataset.role = item.role;
            chip.textContent = item.label;
            row.appendChild(chip);
            const tx = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            tx.className = "editor-type-option-text";
            const name = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            name.className = "editor-type-option-name";
            name.textContent = item.label;
            const desc = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            desc.className = "editor-type-option-desc";
            desc.textContent = item.desc;
            tx.appendChild(name);
            tx.appendChild(desc);
            row.appendChild(tx);
            row.addEventListener("click", (ev) => {
                ev.stopPropagation();
                closeTypePopover();
                if (typeof onPick === "function")
                    onPick(item.role);
            });
            list.appendChild(row);
            allRows.push({ row, header, item });
        }
    }
    // Search filter
    search.addEventListener("input", () => {
        const q = search.value.trim().toLowerCase();
        const headers = list.querySelectorAll(".editor-type-section-header");
        if (!q) {
            for (const { row, header } of allRows) {
                row.classList.remove("hidden");
                header.classList.remove("hidden");
            }
            for (const h of headers)
                h.classList.remove("hidden");
            return;
        }
        for (const h of headers)
            h.classList.add("hidden");
        for (const { row, item } of allRows) {
            const match = item.role.toLowerCase().includes(q)
                || item.label.toLowerCase().includes(q)
                || item.desc.toLowerCase().includes(q);
            row.classList.toggle("hidden", !match);
        }
    });
    document.body.appendChild(pop); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    // Position
    const r = anchor.getBoundingClientRect();
    pop.style.top = `${Math.min(window.innerHeight - 360, r.bottom + 4)}px`; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    pop.style.left = `${Math.min(window.innerWidth - 320, r.left)}px`; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    _activePopover = pop;
    // Outside click + Escape close
    setTimeout(() => {
        const onDoc = (ev) => {
            const target = ev.target;
            if (target && !pop.contains(target) && target !== anchor) {
                closeTypePopover();
                document.removeEventListener("mousedown", onDoc, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            }
        };
        document.addEventListener("mousedown", onDoc, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        const onKey = (ev) => {
            if (ev.key === "Escape") {
                closeTypePopover();
                document.removeEventListener("keydown", onKey, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            }
        };
        document.addEventListener("keydown", onKey, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }, 0);
    // Focus the search box for type-to-filter
    setTimeout(() => search.focus(), 0);
    return pop;
}
export function closeTypePopover() {
    if (_activePopover?.parentNode) {
        _activePopover.parentNode.removeChild(_activePopover);
    }
    _activePopover = null;
}
export function openIssuePopover(opts) {
    closeTypePopover();
    const { anchor, card, issueDesc, onOp } = opts;
    if (!anchor || !card || !issueDesc)
        return null;
    const pop = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    pop.className = "editor-issue-popover is-open";
    pop.setAttribute("role", "dialog");
    pop.dataset.issue = card.issue ?? "";
    const msg = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    msg.className = "editor-issue-popover-message";
    msg.textContent = issueDesc.message;
    pop.appendChild(msg);
    if (issueDesc.fixToRole) {
        const fixToRole = issueDesc.fixToRole;
        const fix = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        fix.type = "button";
        fix.className = "editor-issue-popover-fix";
        fix.textContent = `Change role to ${fixToRole}`;
        fix.addEventListener("click", (ev) => {
            ev.stopPropagation();
            if (typeof onOp === "function") {
                onOp({
                    kind: "set-role",
                    structId: card.structId,
                    before: card.roleToken,
                    after: fixToRole,
                });
            }
            closeTypePopover();
        });
        pop.appendChild(fix);
    }
    document.body.appendChild(pop); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const r = anchor.getBoundingClientRect();
    pop.style.top = `${Math.min(window.innerHeight - 200, r.bottom + 4)}px`; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    pop.style.left = `${Math.min(window.innerWidth - 360, r.left)}px`; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    _activePopover = pop;
    setTimeout(() => {
        const onDoc = (ev) => {
            const target = ev.target;
            if (target && !pop.contains(target) && target !== anchor) {
                closeTypePopover();
                document.removeEventListener("mousedown", onDoc, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            }
        };
        document.addEventListener("mousedown", onDoc, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        const onKey = (ev) => {
            if (ev.key === "Escape") {
                closeTypePopover();
                document.removeEventListener("keydown", onKey, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            }
        };
        document.addEventListener("keydown", onKey, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }, 0);
    return pop;
}
// ─── Drag-to-reorder ────────────────────────────────────────────────────────
/**
 * Wire HTML5 drag-and-drop on the card list. Group-aware:
 *
 *   - If the dragged card is part of a multi-selection, ALL selected cards
 *     move together as a contiguous block to the drop position; their
 *     relative order is preserved.
 *   - Otherwise, only the dragged card moves.
 *
 * On drop, calls `onReorder(movedIds[], beforeStructId | null)`. The host
 * computes the new sibling array and dispatches a `reorder-reading` op.
 *
 * Drop-target indicator: `.drag-over-above` / `.drag-over-below` classes are
 * toggled on the card-currently-hovered. We DON'T treat any selected card as
 * a valid drop target (you can't drop the group on itself), so the indicator
 * skips them.
 *
 * @param {HTMLElement} container
 * @param {Array} cards  unused at runtime; kept for callers that pass it
 *                       defensively. Selection is read live via getSelectedIds.
 * @param {(movedIds: string[], beforeStructId: string|null) => void} onReorder
 * @param {() => Set<string>} getSelectedIds  live accessor for the current
 *                                            selection.
 */
function wireDragAndDrop(container, _cards, onReorder, getSelectedIds) {
    let dragSrcId = null;
    let dragGroup = [];
    container.addEventListener("dragstart", (ev) => {
        const t = ev.target;
        const card = t instanceof Element ? t.closest(".editor-card") : null;
        if (!card)
            return;
        dragSrcId = card.dataset.structId ?? null;
        if (!dragSrcId)
            return;
        // Resolve the dragged group: if the source is part of a multi-
        // selection, the whole selection moves together. Order is the
        // card-list document order, not the click order.
        const sel = (typeof getSelectedIds === "function") ? getSelectedIds() : null;
        if (sel && sel.size >= 2 && sel.has(dragSrcId)) {
            const ordered = [];
            for (const c of Array.from(container.querySelectorAll(".editor-card"))) {
                const id = c.dataset.structId;
                if (id && sel.has(id))
                    ordered.push(id);
            }
            dragGroup = ordered;
        }
        else {
            dragGroup = [dragSrcId];
        }
        // Style every card in the group.
        for (const id of dragGroup) {
            const el = container.querySelector(`.editor-card[data-struct-id="${cssEscape(id)}"]`);
            if (el)
                el.classList.add("is-dragging");
        }
        if (ev.dataTransfer) {
            ev.dataTransfer.effectAllowed = "move";
            try {
                ev.dataTransfer.setData("text/plain", dragGroup.join(","));
            }
            catch { /* IE-quirks tolerated */ }
        }
    });
    container.addEventListener("dragend", () => {
        if (!dragSrcId)
            return;
        for (const id of dragGroup) {
            const el = container.querySelector(`.editor-card[data-struct-id="${cssEscape(id)}"]`);
            if (el)
                el.classList.remove("is-dragging");
        }
        for (const c of Array.from(container.querySelectorAll(".drag-over-above, .drag-over-below"))) {
            c.classList.remove("drag-over-above", "drag-over-below");
        }
        dragSrcId = null;
        dragGroup = [];
    });
    container.addEventListener("dragover", (ev) => {
        if (!dragSrcId)
            return;
        const t = ev.target;
        const target = t instanceof Element ? t.closest(".editor-card") : null;
        if (!target)
            return;
        // Can't drop the group onto a card that's IN the group.
        const targetId = target.dataset.structId;
        if (targetId && dragGroup.includes(targetId))
            return;
        ev.preventDefault();
        if (ev.dataTransfer)
            ev.dataTransfer.dropEffect = "move";
        const r = target.getBoundingClientRect();
        const above = ev.clientY < r.top + r.height / 2;
        for (const c of Array.from(container.querySelectorAll(".drag-over-above, .drag-over-below"))) {
            c.classList.remove("drag-over-above", "drag-over-below");
        }
        target.classList.add(above ? "drag-over-above" : "drag-over-below");
    });
    container.addEventListener("drop", (ev) => {
        if (!dragSrcId)
            return;
        const t = ev.target;
        const target = t instanceof Element ? t.closest(".editor-card") : null;
        if (!target)
            return;
        const targetId = target.dataset.structId;
        if (targetId && dragGroup.includes(targetId))
            return;
        ev.preventDefault();
        const r = target.getBoundingClientRect();
        const above = ev.clientY < r.top + r.height / 2;
        const beforeId = above ? (target.dataset.structId ?? null) : nextSiblingStructId(target);
        if (typeof onReorder === "function") {
            // Wire shape: (movedIds: string[], beforeStructId: string|null).
            // The dragGroup includes the source card plus any other selected
            // cards on the same page (multi-select group drag).
            onReorder(dragGroup.slice(), beforeId);
        }
    });
}
function nextSiblingStructId(cardEl) {
    let n = cardEl.nextElementSibling;
    while (n && !n.classList.contains("editor-card"))
        n = n.nextElementSibling;
    return n ? (n.dataset.structId ?? null) : null;
}
